/**
 * MyCopro - Request Store
 * Gestion des demandes (signalements, étiquettes, fibre)
 * ✅ MISE À JOUR: Ajout reopenRequest pour réouverture après clôture
 * ✅ MISE À JOUR: Sélecteur selectPrintableLabelRequests pour impression batch
 */

import { create } from 'zustand';
import { 
  Request, 
  RequestType, 
  RequestStatus,
  RequestVisibility,
  RequestFilters,
  UserCategoryType,
  filterRequestsForUser
} from '../types';

// ============================================================================
// TYPES DU STORE
// ============================================================================

interface RequestState {
  // Liste des demandes
  requests: Request[];
  
  // Demande sélectionnée (pour détail/édition)
  selectedRequest: Request | null;
  
  // État de chargement
  isLoading: boolean;
  error: string | null;
  
  // Filtres
  filters: RequestFilters;
}

interface RequestActions {
  // Chargement
  setRequests: (requests: Request[]) => void;
  setSelectedRequest: (request: Request | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // CRUD
  addRequest: (request: Request) => void;
  updateRequest: (id: string, data: Partial<Request>) => void;
  removeRequest: (id: string) => void;
  
  // Actions de workflow
  validateRequest: (id: string, validatedBy: string, validatedByName: string) => void;
  transmitRequest: (id: string, transmittedTo: 'syndic' | 'bailleur', bailleurId?: string) => void;
  rejectRequest: (id: string, rejectedBy: string, rejectedByName: string, reason: string) => void;
  closeRequest: (id: string, closedBy: string, closedByName: string, reason: string) => void;
  // ✅ NOUVEAU: Réouverture d'une demande clôturée (pour modification / réimpression)
  reopenRequest: (id: string, reopenedBy: string, reopenedByName: string, reason?: string) => void;
  
  // Filtres
  setFilters: (filters: Partial<RequestFilters>) => void;
  resetFilters: () => void;
  
  // Reset
  reset: () => void;
}

type RequestStore = RequestState & RequestActions;

// ============================================================================
// ÉTAT INITIAL
// ============================================================================

const initialFilters: RequestFilters = {
  status: 'all',
  type: 'all',
  visibility: 'all'
};

const initialState: RequestState = {
  requests: [],
  selectedRequest: null,
  isLoading: false,
  error: null,
  filters: initialFilters
};

// ============================================================================
// STORE
// ============================================================================

export const useRequestStore = create<RequestStore>((set, get) => ({
  ...initialState,

  // ========================================
  // CHARGEMENT
  // ========================================
  
  setRequests: (requests) => {
    set({ requests, isLoading: false, error: null });
  },

  setSelectedRequest: (selectedRequest) => {
    set({ selectedRequest });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  // ========================================
  // CRUD
  // ========================================
  
  addRequest: (request) => {
    set(state => ({
      requests: [request, ...state.requests]
    }));
  },

  updateRequest: (id, data) => {
    set(state => ({
      requests: state.requests.map(r => 
        r.id === id 
          ? { ...r, ...data, updatedAt: new Date().toISOString() } 
          : r
      ),
      selectedRequest: state.selectedRequest?.id === id 
        ? { ...state.selectedRequest, ...data, updatedAt: new Date().toISOString() }
        : state.selectedRequest
    }));
  },

  removeRequest: (id) => {
    set(state => ({
      requests: state.requests.filter(r => r.id !== id),
      selectedRequest: state.selectedRequest?.id === id ? null : state.selectedRequest
    }));
  },

  // ========================================
  // ACTIONS DE WORKFLOW
  // ========================================
  
  validateRequest: (id, validatedBy, validatedByName) => {
    const now = new Date().toISOString();
    get().updateRequest(id, {
      status: 'validated',
      validatedBy,
      validatedByName,
      validatedAt: now.split('T')[0]
    });
  },

  transmitRequest: (id, transmittedTo, bailleurId) => {
    const now = new Date().toISOString();
    get().updateRequest(id, {
      status: 'transmitted',
      transmittedAt: now.split('T')[0],
      transmittedTo,
      bailleurId
    });
  },

  rejectRequest: (id, rejectedBy, rejectedByName, reason) => {
    get().updateRequest(id, {
      status: 'rejected',
      rejectedBy,
      rejectedByName,
      rejectionReason: reason
    });
  },

  closeRequest: (id, closedBy, closedByName, reason) => {
    const now = new Date().toISOString();
    get().updateRequest(id, {
      status: 'closed',
      closedBy,
      closedByName,
      closedAt: now.split('T')[0],
      closedReason: reason
    });
  },

  // ✅ NOUVEAU: Réouverture d'une demande clôturée
  // Remet la demande en statut 'pending' pour permettre modification et réimpression
  // Nettoie les champs de clôture et enregistre qui a réouvert + motif
  reopenRequest: (id, reopenedBy, reopenedByName, reason) => {
    const now = new Date().toISOString();
    get().updateRequest(id, {
      status: 'pending',
      reopenedBy,
      reopenedByName,
      reopenedAt: now.split('T')[0],
      reopenedReason: reason || 'Réouverture pour modification / réimpression',
      // Reset les champs de clôture
      closedBy: undefined,
      closedByName: undefined,
      closedAt: undefined,
      closedReason: undefined
    });
  },

  // ========================================
  // FILTRES
  // ========================================
  
  setFilters: (filters) => {
    set(state => ({
      filters: { ...state.filters, ...filters }
    }));
  },

  resetFilters: () => {
    set({ filters: initialFilters });
  },

  // ========================================
  // RESET
  // ========================================
  
  reset: () => {
    set(initialState);
  }
}));

// ============================================================================
// SÉLECTEURS
// ============================================================================

export const selectRequests = (state: RequestStore) => state.requests;
export const selectSelectedRequest = (state: RequestStore) => state.selectedRequest;
export const selectIsLoading = (state: RequestStore) => state.isLoading;
export const selectError = (state: RequestStore) => state.error;
export const selectFilters = (state: RequestStore) => state.filters;

// Sélecteurs filtrés
export const selectFilteredRequests = (state: RequestStore): Request[] => {
  let filtered = state.requests;
  const { filters } = state;
  
  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(r => r.status === filters.status);
  }
  
  if (filters.type && filters.type !== 'all') {
    filtered = filtered.filter(r => r.type === filters.type);
  }
  
  if (filters.visibility && filters.visibility !== 'all') {
    filtered = filtered.filter(r => r.visibility === filters.visibility);
  }
  
  if (filters.submittedBy) {
    filtered = filtered.filter(r => r.submittedBy === filters.submittedBy);
  }
  
  return filtered;
};

// Sélecteur avec permissions utilisateur
export const selectRequestsForUser = (
  userUid: string, 
  userCategory: UserCategoryType
) => (state: RequestStore): Request[] => {
  return filterRequestsForUser(state.requests, userUid, userCategory);
};

// Sélecteurs de comptage
export const selectRequestCount = (state: RequestStore): number => 
  state.requests.length;

export const selectPendingRequests = (state: RequestStore): Request[] => 
  state.requests.filter(r => r.status === 'pending');

export const selectPendingRequestsCount = (state: RequestStore): number => 
  state.requests.filter(r => r.status === 'pending').length;

export const selectPendingPublicRequests = (state: RequestStore): Request[] => 
  state.requests.filter(r => r.status === 'pending' && r.visibility === 'public');

export const selectPendingPublicRequestsCount = (state: RequestStore): number => 
  state.requests.filter(r => r.status === 'pending' && r.visibility === 'public').length;

export const selectTransmittedRequests = (state: RequestStore): Request[] => 
  state.requests.filter(r => r.status === 'transmitted');

// Sélecteur par statut
export const selectRequestsByStatus = (status: RequestStatus) => (state: RequestStore): Request[] => 
  state.requests.filter(r => r.status === status);

// Sélecteur par type
export const selectRequestsByType = (type: RequestType) => (state: RequestStore): Request[] => 
  state.requests.filter(r => r.type === type);

// Sélecteur par ID
export const selectRequestById = (id: string) => (state: RequestStore): Request | undefined => 
  state.requests.find(r => r.id === id);

// Demandes de l'utilisateur courant
export const selectUserRequests = (userUid: string) => (state: RequestStore): Request[] => 
  state.requests.filter(r => r.submittedBy === userUid);

// ✅ NOUVEAU: Sélecteur des demandes d'étiquettes imprimables
// Retourne les étiquettes en statut 'pending' ou 'validated' (prêtes pour impression batch)
export const selectPrintableLabelRequests = (state: RequestStore): Request[] =>
  state.requests.filter(r => 
    r.type === 'label' && 
    (r.status === 'pending' || r.status === 'validated')
  );
